import { useTranslation } from 'react-i18next';
import { Dropdown, DropdownTriggerButton, DropdownContent, DropDownSelectableContent } from '../Dropdown';

interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
}: PaginationProps) {
  const { t } = useTranslation();
  const totalPages = Math.ceil(total / pageSize);
  const canGoPrevious = page > 1;
  const canGoNext = page < totalPages;

  return (
    <div className="flex flex-wrap justify-between items-center gap-2 text-onsurface">
      <div className="flex items-center gap-x-2">
        <p className="text-sm text-onsurface">
          <span className="font-medium">{total}</span>{' '}
          <span className="opacity-60">
            {t('companies.pagination.results')}
          </span>
        </p>

        <Dropdown >
          <DropdownTriggerButton>
            {pageSize} {t('companies.pagination.perPage')}
            <svg
              className="shrink-0 size-3.5"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </DropdownTriggerButton>

          <DropdownContent>
            {PaginationDropDownContent(onPageSizeChange, pageSize)}
          </DropdownContent>

        </Dropdown>
      </div>

      <nav
        className="flex justify-end items-center gap-x-1"
        aria-label={t('companies.pagination.label')}
      >
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={!canGoPrevious}
          className="min-h-9.5 min-w-9.5 py-2 px-2.5 inline-flex justify-center items-center gap-x-2 text-sm rounded-lg text-onsurface hover:bg-surface-darker disabled:opacity-50 disabled:pointer-events-none focus:outline-hidden focus:bg-surface-darker"
          aria-label={t('companies.pagination.previous')}
        >
          <svg
            className="shrink-0 size-3.5"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
          <span className="sr-only">{t('companies.pagination.previous')}</span>
        </button>

        <div className="flex items-center gap-x-1">
          <span className="min-h-9.5 min-w-9.5 flex justify-center items-center bg-surface-darker text-onsurface py-2 px-3 text-sm rounded-lg disabled:opacity-50 disabled:pointer-events-none">
            {page}
          </span>
          <span className="min-h-9.5 flex justify-center items-center opacity-60 py-2 px-1.5 text-sm">
            {t('companies.pagination.of')}
          </span>
          <span className="min-h-9.5 flex justify-center items-center opacity-60 py-2 px-1.5 text-sm">
            {totalPages || 1}
          </span>
        </div>

        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={!canGoNext}
          className="min-h-9.5 min-w-9.5 py-2 px-2.5 inline-flex justify-center items-center gap-x-2 text-sm rounded-lg text-onsurface hover:bg-surface-darker disabled:opacity-50 disabled:pointer-events-none focus:outline-hidden focus:bg-surface-darker"
          aria-label={t('companies.pagination.next')}
        >
          <span className="sr-only">{t('companies.pagination.next')}</span>
          <svg
            className="shrink-0 size-3.5"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m9 18 6-6-6-6" />
          </svg>
        </button>
      </nav>
    </div>
  );
}


export default Pagination;


function PaginationDropDownContent(onPageSizeChange: (pageSize: number) => void, pageSize: number) {

  return <div className="p-1">
    {PAGE_SIZE_OPTIONS.map((size) => (


      <DropDownSelectableContent
        value={size.toString()}
        isSelected={pageSize === size}
        doSelect={() => {

          onPageSizeChange(size);

        }}
      />
    ))}
  </div>;
}

